// src/components/Home.js
import React, { useState } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import ClickableLink from './ClickableLink';

const Home = () => {
    const { trackLinkClick, trackResumeView, trackSocialClick, trackContactAttempt } = useAnalytics();

    // Handle resume click with version tracking
    const handleResumeClick = (href) => {
        let version = 'unknown';
        if (href.includes('quant')) version = 'quant';
        else if (href.includes('swe')) version = 'swe';
        else if (href.includes('robotics')) version = 'robotics';
        
        trackResumeView(version);
        trackLinkClick(href, 'Resume');
    };

    const [isEmailHovered, setIsEmailHovered] = useState(false);
    return <div className="flex flex-col flex-initial self-start justify-center items-left min-h-screen py-8 md:py-0 space-y-4">
        {/* Heading  */}
        <div className="md:text-custom-80 text-4xl sm:text-5xl text-balance font-black font-sans flex flex-row items-start md:items-center justify-between">
            <div className="grow">
                <span className="block mb-4">Hi, I'm Ethan.</span>
                <div className="font-semibold text-base sm:text-lg">A rising senior at Penn, studying Computer Science, Math, Music, and Robotics.</div>
                <div className="font-semibold text-base sm:text-lg">Avid coder, musician, problem-solver.</div>
            </div>
            <img src={'/candid copy.jpeg'} alt="Ethan" className="w-32 h-28 sm:w-36 sm:h-32 md:w-45 md:h-40 object-cover rounded-lg flex-shrink-0 self-end sm:self-center md:self-center" />
        </div>

        {/* Work / Research section */}
        <div className='font-medium text-base sm:text-lg'>Summer 2025, I'm working on the{' '}
            <ClickableLink 
                href='https://openai.com/careers/research-engineer-retrieval-and-search-applied-engineering/'
                onClick={() => trackLinkClick('https://openai.com/careers/research-engineer-retrieval-and-search-applied-engineering/', 'ChatGPT Search Team')}
            >
                ChatGPT Search Team
            </ClickableLink> @ OpenAI, advancing multimodal inference capabilities.
            At the same time, I'm collaborating with the <span></span>
            <ClickableLink 
                href='https://www.grasp.upenn.edu/research-groups/perception-action-learning-group/'
                onClick={() => trackLinkClick('https://www.grasp.upenn.edu/research-groups/perception-action-learning-group/', 'PAL Group')}
            >
                {' '}PAL Group
            </ClickableLink> and <span></span>
            <ClickableLink 
                href='https://www.csail.mit.edu/research/robust-robotics-group'
                onClick={() => trackLinkClick('https://www.csail.mit.edu/research/robust-robotics-group', 'MIT CSAIL')}
            >
                {' '}MIT CSAIL
            </ClickableLink>, exploring ways to better incorporate uncertainty into model-based RL.
        </div>
        <div className='font-medium text-base sm:text-lg'>I'm also an amateur{' '}
            <ClickableLink 
                href='https://music.sas.upenn.edu/performance/marian-anderson-performance-program'
                onClick={() => trackLinkClick('https://music.sas.upenn.edu/performance/marian-anderson-performance-program', 'pianist')}
            >
                pianist
            </ClickableLink> and <span></span>
            violinist. During concert season, I'm coached by <span></span>
            <ClickableLink 
                href='https://music.sas.upenn.edu/ensembles/penn-chamber'
                onClick={() => trackLinkClick('https://music.sas.upenn.edu/ensembles/penn-chamber', 'Penn Chamber')}
            >
                Penn Chamber
            </ClickableLink> 
            <span></span>'s director Thomas Kraines with my <span></span>
            <ClickableLink 
                href='https://www.youtube.com/watch?v=TuAFTmtQ0X4'
                onClick={() => trackLinkClick('https://www.youtube.com/watch?v=TuAFTmtQ0X4', 'piano quintet')}
            >
                piano quintet
            </ClickableLink>, and led by Maestro Thomas Hong as a concertmaster for the <span></span>
            <ClickableLink 
                href='https://music.sas.upenn.edu/ensembles/penn-symphony-orchestra'
                onClick={() => trackLinkClick('https://music.sas.upenn.edu/ensembles/penn-symphony-orchestra', 'Penn Symphony Orchestra')}
            >
                Penn Symphony Orchestra
            </ClickableLink>.</div>
        <div className='font-medium text-base sm:text-lg'>Balancing bits and beats. If that sounds intriguing, let's connect.</div>

        {/* CTA Links */}

        <div className='flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0'>
            <ClickableLink 
                href={Math.random() > 2.0 / 3.0 ? '/resume_quant.pdf' : (Math.random() > 0.5 ? '/resume_swe.pdf' : '/resume_robotics.pdf')} 
                onClick={(href) => handleResumeClick(href)}
            >
                <div className='font-semibold text-lg'>Resume</div>
            </ClickableLink>
            <div className='hidden sm:block w-4'></div>
            <div className='hidden sm:block font-semibold text-lg'>|</div>
            <div className='hidden sm:block w-4'></div>
            <ClickableLink 
                href='https://www.linkedin.com/in/ethan-r-yu/' 
                onClick={() => {
                    trackSocialClick('LinkedIn');
                    trackLinkClick('https://www.linkedin.com/in/ethan-r-yu/', 'LinkedIn');
                }}
            >
                <div className='font-semibold text-lg'>LinkedIn</div>
            </ClickableLink>
            <div className='hidden sm:block w-4'></div>
            <div className='hidden sm:block font-semibold text-lg'>|</div>
            <div className='hidden sm:block w-4'></div>
            <ClickableLink 
                href='mailto:ethan@openai.com' 
                onClick={() => {
                    trackContactAttempt('email', { address: 'ethan@openai.com' });
                    trackLinkClick('mailto:ethan@openai.com', 'Email');
                }}
                onMouseEnter={() => setIsEmailHovered(true)}
                onMouseLeave={() => setIsEmailHovered(false)}
            >
                <div className='font-semibold text-lg'>{isEmailHovered ? 'ethan@openai.com' : 'Email'}</div>
            </ClickableLink>
        </div>
    </div>;
};

export default Home;
